import React, { useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import { deserialize, update } from 'serializr';

import { DISPATCHED, EntityList, Job } from '@vromo/models';

import styled from '@emotion/styled';
import Bugsnag from '@vromo/analytics/bugsnag';
import { RootStore } from '@vromo/dashboard/src/stores/RootStore';
import { useStore } from '@vromo/mobx-react/mobx-react';
import { CourierInfo, getReturnReason } from '@vromo/models/CourierInfo';
import Button from '@vromo/ui/components/Button';
import Modal, { ModalActions, ModalContent, ModalHeader } from '@vromo/ui/components/Modal';
import Title from '@vromo/ui/components/Title';
import { runInAction } from 'mobx';
import AuditHistoryItem from './AuditHistoryItem';

const NoHistory = styled.div`
    height: 30vw;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Strong = styled.span`
    font-weight: bold;
`;

const HeaderItem = ({ value, title }: { value?: string | number | null; title: string }) =>
    (!!value && (
        <div>
            {`${title} `}
            <Strong>{value}</Strong>
        </div>
    )) ||
    null;

interface AuditHistoryModalProps {
    isOpen: boolean;
    selectedJob?: Job;
    onClose: () => void;
}

export default observer<AuditHistoryModalProps>(function AuditHistoryModal({
    isOpen,
    selectedJob,
    onClose
}) {
    const [courierInfo, setCourierInfo] = useState<EntityList<CourierInfo> | null>(null);
    const { authStore, jobStore, vehicleStore } = useStore<RootStore>('rootStore');

    const wasOfferedToThirdParty = () => {
        if (!selectedJob?.dispatches?.items) return false;
        const latestDispatch = [...selectedJob?.dispatches?.items]
            .reverse()
            .find((item) => item.type === DISPATCHED);
        return vehicleStore.withId(latestDispatch?.vehicle?.id)?.synthetic;
    };

    useEffect(() => {
        if (!isOpen) return;
        if (!wasOfferedToThirdParty()) return;
        const { graph, role } = authStore;
        const fetchJobs = graph.root.graph().roles(role.id).jobs(`${selectedJob?.id}`).get();

        (async () => {
            try {
                const detailedJob = await fetchJobs;
                setCourierInfo(detailedJob?.courierInfo || null);
                const jobWithAllDetails = deserialize(Job, detailedJob);
                runInAction(() => {
                    update(jobStore.jobs, { items: [jobWithAllDetails], type: 'update' });
                });
            } catch (err) {
                console.error(err);
                err.message = `data fetch error on job details panel. ${err.toString()}`;
                Bugsnag.notify(err);
            }
        })();
    }, [isOpen]);

    function renderAuditContent() {
        const job = deserialize(Job, selectedJob);
        const courierInfoList = courierInfo?.items;

        return job?.dispatches?.items.map((d) => {
            const returnReason = getReturnReason(courierInfoList, d);

            return <AuditHistoryItem key={d.id} dispatch={d} returnReason={returnReason} />;
        });
    }

    function renderEmptyContent() {
        return <NoHistory>No audit history to show.</NoHistory>;
    }

    const { name, id } = selectedJob || {};

    return (
        <Modal isOpen={isOpen} onRequestClose={onClose}>
            <ModalHeader>
                <Title>Audit History</Title>
                <HeaderItem value={name} title="Job name:" />
                <HeaderItem value={id} title="Job id:" />
            </ModalHeader>
            <ModalContent>
                {selectedJob?.dispatches?.items.length
                    ? renderAuditContent()
                    : renderEmptyContent()}
            </ModalContent>
            <ModalActions>
                <Button onClick={onClose}>Close</Button>
            </ModalActions>
        </Modal>
    );
});
